import { IconAlertCircle, IconClock } from '@developerz/ui';
import { A } from '@solidjs/router';
import { createSignal, type JSX, Match, Show, Switch } from 'solid-js';
import { t } from '../i18n';
import { orgHref } from '../lib/org';
import {
  type ProjectSetupOutcome,
  projectSetupCall,
  setupRefusalKey,
  startProjectSetup,
} from '../lib/project-setup';
import { CopyAsCall } from './CopyAsCall';
import { Panel } from './Panel';
import { Button } from './ui/Button';

// Repo Settings → establish the base (#1728). The human door onto
// `POST /v1/repos/:owner/:repo/setup` and the MCP `project setup` action.
//
// IT LIVES ON THE REPO, not in the onboarding funnel the capability manifest's
// gap text named. The verb is per-repo and the funnel is account-wide, so a
// funnel step would need its own repo picker — and a repo enabled six months
// after onboarding would never reach it. Settled on #1728 before this was built.
//
// NO ROLE MIRROR. It spends `write:tasks`, the same scope the queue board's New
// task spends, and that form mirrors nothing either — a refused dispatch renders
// the API's own 403 inline rather than a control that silently was not there.
// Deliberately NOT `write:repos`: a credential that may enable a repo is not
// thereby allowed to spend the account's inference budget on it.
//
// A 202 IS NOT AN ESTABLISHED BASE, so the result wears the waiting icon and
// points at the QUEUE BOARD — the only surface that can show the task before a
// box has claimed it. Nothing here re-reads the repo: the session lands minutes
// later, on the box, and refetching would ask the server to confirm that nothing
// happened yet.

export function RepoSetupPanel(props: {
  readonly fullName: string;
  readonly token: string | null;
}): JSX.Element {
  const [busy, setBusy] = createSignal(false);
  const [outcome, setOutcome] = createSignal<ProjectSetupOutcome | null>(null);

  const dispatch = async (): Promise<void> => {
    if (busy()) return;
    setBusy(true);
    setOutcome(await startProjectSetup(props.fullName, props.token));
    setBusy(false);
  };

  const of = <K extends ProjectSetupOutcome['kind']>(
    kind: K,
  ): Extract<ProjectSetupOutcome, { kind: K }> | null => {
    const result = outcome();
    return result?.kind === kind ? (result as Extract<ProjectSetupOutcome, { kind: K }>) : null;
  };

  return (
    <Panel
      title={t('repo_detail.setup_title')}
      action={
        <Button variant="secondary" size="sm" disabled={busy()} onClick={() => void dispatch()}>
          {t(busy() ? 'repo_detail.setup_dispatching' : 'repo_detail.setup_dispatch')}
        </Button>
      }
    >
      <p class="repo-settings__note">{t('repo_detail.setup_desc')}</p>

      <Switch>
        <Match when={of('started')}>
          {(o) => (
            <div class="alert alert--icon" role="alert">
              <IconClock />
              <p class="alert__title">{t('repo_detail.setup_started')}</p>
              <p class="alert__desc">
                {t('repo_detail.setup_started_desc', { task: o().task_id })}
              </p>
              <A class="alert__action" href={orgHref('/fleet')}>
                {t('repo_detail.setup_open_queue')}
              </A>
            </div>
          )}
        </Match>

        {/* A 2xx that named no task: a session may still have been dispatched,
            so neither "started" nor "try again" is honest. */}
        <Match when={of('unclear')}>
          <div class="alert alert--icon" role="alert">
            <IconAlertCircle />
            <p class="alert__title">{t('repo_detail.setup_unclear')}</p>
            <A class="alert__action" href={orgHref('/fleet')}>
              {t('repo_detail.setup_open_queue')}
            </A>
          </div>
        </Match>

        <Match when={of('refused')}>
          {(o) => (
            <div class="alert alert--icon alert--destructive" role="alert">
              <IconAlertCircle />
              <p class="alert__title">{t(setupRefusalKey(o().status))}</p>
              <Show when={o().detail}>{(detail) => <p class="alert__desc">{detail()}</p>}</Show>
            </div>
          )}
        </Match>
      </Switch>

      <CopyAsCall
        calls={[{ label: t('repo_detail.setup_dispatch'), call: projectSetupCall(props.fullName) }]}
      />
    </Panel>
  );
}
